import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { fetchUnreadCount } from "@/lib/whatsNewApi";

// Header bell with unread badge. Polls /api/whats-new/unread-count on mount,
// on tab focus, and whenever the user marks something read elsewhere.
export default function WhatsNewBell() {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { unread } = await fetchUnreadCount();
      setCount(unread);
    } catch {
      /* ignore — bell shouldn't crash header */
    }
  }, []);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("whats-new-read", handler);
    window.addEventListener("focus", handler);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") refresh();
    });
    // Light poll every 60s in case a release lands while page is open
    const interval = setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener("whats-new-read", handler);
      window.removeEventListener("focus", handler);
      clearInterval(interval);
    };
  }, [refresh]);

  return (
    <Link
      to="/whats-new"
      className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-gray-100 transition-colors"
      aria-label="What's New"
      data-testid="whats-new-bell"
      title={count > 0 ? `${count} new update${count > 1 ? "s" : ""}` : "What's New"}
    >
      <Bell className="h-5 w-5 text-gray-600" />
      {count > 0 && (
        <span
          className="absolute top-0.5 right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-white"
          data-testid="whats-new-bell-badge"
        >
          {count > 9 ? "9+" : count}
        </span>
      )}
    </Link>
  );
}
